import React, { useEffect, useState } from "react";
import { Gift, Sparkles, ShoppingBag, CheckCircle2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * GiftShop — redeem points for rewards.
 *
 * Endpoints:
 *   GET /api/giftshop/catalog            → list of items
 *   POST /api/giftshop/redeem            → { item_id }
 *   GET /api/me/points                   → balance + rules + ledger
 *   GET /api/me/giftshop/redemptions     → past redemptions
 */
export default function GiftShop() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [points, setPoints] = useState({ balance: 0, dollars_value: 0, rules: {}, ledger: [] });
  const [redemptions, setRedemptions] = useState([]);
  const [redeemingId, setRedeemingId] = useState(null);
  const [justRedeemed, setJustRedeemed] = useState(null);

  const load = async () => {
    try {
      const [c, p, r] = await Promise.all([
        api.get("/giftshop/catalog"),
        user ? api.get("/me/points") : Promise.resolve({ data: null }),
        user ? api.get("/me/giftshop/redemptions") : Promise.resolve({ data: [] }),
      ]);
      setCatalog(c.data || []);
      if (p.data) setPoints(p.data);
      setRedemptions(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.user_id]);

  const redeem = async (item) => {
    if (!user) { toast.error("Sign in to redeem"); return; }
    if (points.balance < item.points_cost) {
      toast.error(`Need ${item.points_cost - points.balance} more points`);
      return;
    }
    if (!window.confirm(`Redeem "${item.name}" for ${item.points_cost} points?`)) return;
    setRedeemingId(item.id);
    try {
      const { data } = await api.post("/giftshop/redeem", { item_id: item.id });
      setPoints((p) => ({ ...p, balance: data.new_balance, dollars_value: (data.new_balance * (p.point_value_usd || 0.01)).toFixed(2) }));
      setJustRedeemed(item);
      toast.success(`Redeemed ${item.name}!`, { id: "gift-redeem" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRedeemingId(null);
    }
  };

  return (
    <div data-testid="giftshop-page" className="min-h-screen pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 px-3 sm:px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb + back to Store */}
        <Link
          to="/shop"
          data-testid="giftshop-back"
          className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Store
        </Link>

        {/* Hero */}
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden p-5 sm:p-8 md:p-10 bg-gradient-to-br from-yellow-500/20 via-amber-500/15 to-orange-500/10 border border-yellow-400/30">
          <div className="flex flex-col md:flex-row md:items-end gap-5 md:gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-yellow-300 uppercase tracking-[0.3em] text-xs sm:text-sm font-black">
                <Gift className="w-4 h-4" /> Gift Shop
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white tracking-tighter leading-[1.05] mt-1">
                Points. Rewards.<br className="hidden md:block" /> Redeemed.
              </h1>
              <p className="text-white/70 text-sm sm:text-base mt-2 max-w-xl">
                Earn points for posting, liking, commenting, tipping creators, and going live. Spend them here on store credit, swag, or subscription time.
              </p>
            </div>

            {/* Balance card */}
            {user && (
              <div
                data-testid="giftshop-balance-card"
                className="w-full md:w-64 bg-black/40 border border-yellow-400/30 rounded-2xl p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 font-black mb-1">Your balance</div>
                <div data-testid="giftshop-balance" className="font-display font-black text-4xl sm:text-5xl text-white tabular-nums leading-none">
                  {points.balance.toLocaleString()}
                </div>
                <div className="text-[11px] text-white/50 mt-1">pts · ≈ ${points.dollars_value} USD</div>
              </div>
            )}
          </div>
        </div>

        {/* Earning rules cheat-sheet */}
        {Object.keys(points.rules || {}).length > 0 && (
          <div
            data-testid="giftshop-rules"
            className="mt-5 flex flex-wrap gap-2 text-[11px] sm:text-xs"
          >
            {Object.entries(points.rules).map(([key, val]) => (
              <div
                key={key}
                className="bg-white/5 border border-white/10 rounded-full px-3 py-1 text-white/70"
              >
                <span className="font-bold text-yellow-300">+{val}</span>{" "}
                {key.replace(/_/g, " ")}
              </div>
            ))}
          </div>
        )}

        {/* Catalog grid */}
        <div
          data-testid="giftshop-catalog"
          className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4"
        >
          {catalog.map((item) => {
            const afford = user && points.balance >= item.points_cost;
            const busy = redeemingId === item.id;
            return (
              <div
                key={item.id}
                data-testid={`giftshop-item-${item.id}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-3 sm:p-4 flex flex-col"
              >
                <div className="text-3xl sm:text-4xl md:text-5xl mb-2" role="img" aria-hidden>{item.emoji}</div>
                <div className="font-bold text-white text-sm sm:text-base leading-tight">{item.name}</div>
                <div className="text-white/50 text-[11px] sm:text-xs mt-1 line-clamp-2 flex-1">{item.description}</div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-yellow-300 font-display font-black text-lg sm:text-xl tabular-nums">
                    {item.points_cost.toLocaleString()}
                  </span>
                  <span className="text-white/50 text-[10px] uppercase tracking-wider">pts</span>
                  <span className="ml-auto text-white/40 text-[10px]">${item.usd_value}</span>
                </div>
                <button
                  data-testid={`giftshop-redeem-${item.id}`}
                  disabled={!afford || busy}
                  onClick={() => redeem(item)}
                  className={`mt-2 sm:mt-3 w-full text-xs sm:text-sm font-bold py-2 rounded-full inline-flex items-center justify-center gap-1 transition ${
                    afford
                      ? "bg-yellow-300 hover:bg-yellow-200 text-black"
                      : "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed"
                  }`}
                >
                  {busy
                    ? "Redeeming…"
                    : afford
                      ? <><Sparkles className="w-3.5 h-3.5" /> Redeem</>
                      : user
                        ? `Need ${item.points_cost - points.balance}`
                        : "Sign in"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Recent redemptions */}
        {user && redemptions.length > 0 && (
          <div data-testid="giftshop-redemptions" className="mt-10">
            <h2 className="font-display font-black text-xl sm:text-2xl text-white mb-3">Your Redemptions</h2>
            <ul className="space-y-2">
              {redemptions.slice(0, 10).map((r) => (
                <li
                  key={r.id}
                  data-testid={`giftshop-redemption-${r.id}`}
                  className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate">{r.item_name}</div>
                    <div className="text-[11px] text-white/50">
                      {new Date(r.created_at).toLocaleString()} ·{" "}
                      <span className="capitalize">{r.status.replace(/_/g, " ")}</span>
                    </div>
                  </div>
                  <div className="text-yellow-300 font-black tabular-nums">−{r.points_cost}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Just-redeemed celebration */}
        {justRedeemed && (
          <div
            data-testid="giftshop-just-redeemed"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-emerald-500/20 border border-emerald-500/40 rounded-2xl p-4 backdrop-blur-sm text-center max-w-sm animate-pulse z-50"
          >
            <div className="text-emerald-300 font-bold flex items-center gap-2 justify-center">
              <CheckCircle2 className="w-5 h-5" /> {justRedeemed.name} redeemed!
            </div>
            <button
              onClick={() => setJustRedeemed(null)}
              className="text-white/60 text-xs mt-1 hover:text-white"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Not signed in */}
        {!user && (
          <div className="mt-10 text-center">
            <Link
              to="/signup"
              data-testid="giftshop-signin-cta"
              className="inline-flex items-center gap-2 bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-6 py-3 rounded-full"
            >
              <ShoppingBag className="w-4 h-4" /> Sign up to start earning
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
